import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

/**
 * Country / region awareness.
 *
 * Makes the app location-aware BEFORE checkout so we can:
 *   - filter physical products the shopper can't receive, and
 *   - fetch the right shipping rates for their destination.
 *
 * On load we ask the backend (GET /api/region) for the visitor's detected
 * country (from their IP) plus the list of countries where physical fulfillment
 * is offered. The shopper can override the country at any time; the override is
 * remembered in localStorage. When a country isn't supported, only digital
 * products (storybooks / coloring books as a PDF) are available.
 */

const STORAGE_KEY = 'sv_country';
const serverUrl = process.env.REACT_APP_SERVER_URL;

interface CountryContextType {
	/** Currently selected/detected ISO alpha-2 country code (uppercase). */
	country: string | null;
	/** True while the initial region lookup is in flight. */
	loading: boolean;
	/** Countries where physical fulfillment (print + shipping) is offered. */
	supportedCountries: string[];
	/** Whether the current country supports physical fulfillment. */
	isSupported: boolean;
	/** True once the shopper has explicitly picked a country. */
	userSelected: boolean;
	/** Change the destination country (persists the choice). */
	setCountry: (code: string) => void;
}

const CountryContext = createContext<CountryContextType>({} as CountryContextType);

export const useCountry = () => {
	const ctx = useContext(CountryContext);
	if (!ctx) throw new Error('useCountry must be used within a CountryProvider');
	return ctx;
};

const readStoredCountry = (): string | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw && /^[A-Z]{2}$/.test(raw) ? raw : null;
	} catch {
		return null;
	}
};

export const CountryProvider = ({ children }: { children: React.ReactNode }) => {
	const stored = readStoredCountry();
	const [country, setCountryState] = useState<string | null>(stored);
	const [userSelected, setUserSelected] = useState<boolean>(!!stored);
	const [supportedCountries, setSupportedCountries] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	// Initial region lookup: detected country + supported list.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await fetch(`${serverUrl}/api/region`);
				if (!res.ok) throw new Error(`region ${res.status}`);
				const data = await res.json();
				if (cancelled) return;
				const supported: string[] = Array.isArray(data.supportedCountries)
					? data.supportedCountries.map((c: string) => c.toUpperCase())
					: [];
				setSupportedCountries(supported);
				// Only auto-set the country when the shopper hasn't chosen one.
				setCountryState((prev) => {
					if (prev) return prev;
					const detected = data.detectedCountry ? String(data.detectedCountry).toUpperCase() : null;
					return detected || supported[0] || 'GR';
				});
			} catch {
				if (!cancelled) {
					// Fail safe: assume Greece is supported so the store still works.
					setSupportedCountries((prev) => (prev.length ? prev : ['GR']));
					setCountryState((prev) => prev || 'GR');
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	const setCountry = useCallback((code: string) => {
		const norm = (code || '').toUpperCase();
		if (!/^[A-Z]{2}$/.test(norm)) return;
		setCountryState(norm);
		setUserSelected(true);
		try { localStorage.setItem(STORAGE_KEY, norm); } catch { /* ignore */ }
	}, []);

	const isSupported = useMemo(() => {
		if (!country) return true; // unknown yet → don't restrict prematurely
		if (supportedCountries.length === 0) return true;
		return supportedCountries.includes(country);
	}, [country, supportedCountries]);

	const value = useMemo<CountryContextType>(() => ({
		country,
		loading,
		supportedCountries,
		isSupported,
		userSelected,
		setCountry,
	}), [country, loading, supportedCountries, isSupported, userSelected, setCountry]);

	return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
};

export default CountryContext;
